import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { db } from "@/lib/firebase";
import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, getDoc, setDoc } from "firebase/firestore";
import { toast } from "sonner";
import { logEvent } from "@/lib/analytics";
import { IMAGES } from "@/lib/products";
import {
  Lock, Unlock, Cloud, Smartphone, Sparkles, Palette, Feather, QrCode, Upload, Trash2, Download,
  ChevronRight, ArrowRight, Check, Clock, ShieldCheck,
} from "lucide-react";

const MOODS = ["Happy", "Calm", "Focused", "Low", "Anxious", "Motivated", "Tired", "Grateful"];
const BRUSH_COLORS = ["#3B2B20", "#7C3AED", "#EC4899", "#F59E0B", "#10B981", "#3B82F6", "#EF4444"];
const DIGITAL_PREVIEWS = [
  { title: "Mind Canvas", img: "/digital-preview/mind-canvas-preview.png" },
  { title: "Private Diary", img: "/digital-preview/diary-preview.png" },
  { title: "Memory QR", img: "/digital-preview/memory-qr-preview.png" },
  { title: "Planner Page Upload", img: "/digital-preview/upload-page-preview.png" },
];

// ---------- Diary widget ----------
const DiaryWidget = () => {
  const [title, setTitle] = useState("");
  const [mood, setMood] = useState("Calm");
  const [body, setBody] = useState("");
  const [entries, setEntries] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nc_diary_entries") || "[]"); } catch { return []; }
  });

  const save = () => {
    if (!body.trim()) return toast.error("Write something first");
    const entry = { title: title.trim() || "Untitled", mood, body, ts: Date.now() };
    const next = [entry, ...entries].slice(0, 50);
    setEntries(next);
    localStorage.setItem("nc_diary_entries", JSON.stringify(next));
    setTitle(""); setBody("");
    toast.success("Saved to your diary");
    logEvent("diary_entry_saved", { mood });
  };

  return (
    <div data-testid="diary-widget" className="rounded-2xl border-2 border-espresso bg-cream overflow-hidden">
      <div className="bg-champagne px-4 py-2.5 border-b-2 border-espresso flex items-center gap-2 font-mono text-xs">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-2 text-espresso">diary.txt</span>
      </div>
      <div className="p-6 space-y-3">
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Title for today..." data-testid="diary-title"
          className="w-full bg-ivory border-2 border-espresso rounded-lg px-4 py-2.5 text-sm font-mono outline-none"
        />
        <div className="flex flex-wrap gap-2">
          {MOODS.map((m) => (
            <button
              key={m}
              onClick={() => setMood(m)}
              data-testid={`mood-${m.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono border-2 border-espresso transition-colors ${mood === m ? "bg-gold text-espresso" : "bg-cream text-espresso hover:bg-champagne"}`}
            >
              {m}
            </button>
          ))}
        </div>
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)}
          rows={6} placeholder="Start writing..." data-testid="diary-body"
          className="w-full bg-ivory border-2 border-espresso rounded-lg px-4 py-3 text-sm font-mono outline-none resize-none"
        />
        <button onClick={save} data-testid="diary-save" className="bg-espresso text-cream font-mono text-xs uppercase tracking-wider px-5 py-2.5 rounded-lg hover:bg-espresso-dark transition-colors">
          Save entry
        </button>
        {entries.length > 0 && (
          <div className="pt-4 border-t-2 border-line">
            <div className="text-xs font-mono uppercase text-muted_brown mb-3">Recent entries ({entries.length})</div>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {entries.slice(0, 3).map((e, i) => (
                <div key={i} className="text-xs font-mono text-espresso bg-ivory rounded-lg px-3 py-2 border border-line">
                  <span className="text-muted_brown">[{new Date(e.ts).toLocaleDateString()}]</span> <span className="text-gold">{e.mood}</span> · {e.title}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Canvas doodle widget ----------
const CanvasWidget = () => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [color, setColor] = useState(BRUSH_COLORS[0]);
  const [size, setSize] = useState(3);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    c.width = c.offsetWidth * 2;
    c.height = c.offsetHeight * 2;
    const ctx = c.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.fillStyle = "#FDFBF7";
    ctx.fillRect(0, 0, c.offsetWidth, c.offsetHeight);
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t?.clientX ?? e.clientX) - r.left, y: (t?.clientY ?? e.clientY) - r.top };
  };
  const start = (e) => { drawing.current = true; const p = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.strokeStyle = color; ctx.lineWidth = size; ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const end = () => (drawing.current = false);

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FDFBF7";
    ctx.fillRect(0, 0, c.offsetWidth, c.offsetHeight);
  };
  const download = () => {
    const c = canvasRef.current;
    const link = document.createElement("a");
    link.download = `mind-canvas-${Date.now()}.png`;
    link.href = c.toDataURL("image/png");
    link.click();
    logEvent("canvas_downloaded");
  };

  return (
    <div data-testid="canvas-widget" className="rounded-2xl border-2 border-espresso bg-cream overflow-hidden">
      <div className="bg-champagne px-4 py-2.5 border-b-2 border-espresso flex items-center gap-2 font-mono text-xs">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-2 text-espresso">canvas.exe</span>
      </div>
      <div className="p-4 flex items-center gap-2 border-b-2 border-espresso bg-ivory flex-wrap">
        {BRUSH_COLORS.map((c) => (
          <button
            key={c}
            onClick={() => setColor(c)}
            style={{ backgroundColor: c }}
            data-testid={`brush-${c}`}
            className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-espresso" : "border-cream"}`}
          />
        ))}
        <input type="range" min={1} max={20} value={size} onChange={(e) => setSize(Number(e.target.value))} className="ml-2 flex-1 max-w-[120px]" />
        <button onClick={clear} data-testid="canvas-clear" className="ml-auto w-9 h-9 rounded-lg border-2 border-espresso bg-cream hover:bg-champagne flex items-center justify-center"><Trash2 size={14} /></button>
        <button onClick={download} data-testid="canvas-download" className="w-9 h-9 rounded-lg border-2 border-espresso bg-cream hover:bg-champagne flex items-center justify-center"><Download size={14} /></button>
      </div>
      <canvas
        ref={canvasRef}
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        className="w-full h-72 bg-ivory cursor-crosshair block"
        data-testid="canvas-area"
      />
    </div>
  );
};

// ---------- Upload page widget ----------
const UploadWidget = () => {
  const [preview, setPreview] = useState(null);
  const [pageNo, setPageNo] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [tags, setTags] = useState("");

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(f);
  };

  const save = () => {
    if (!preview) return toast.error("Please upload an image first");
    try {
      const lib = JSON.parse(localStorage.getItem("nc_planner_library") || "[]");
      lib.unshift({ preview, pageNo, title, note, tags: tags.split(",").map(t => t.trim()).filter(Boolean), ts: Date.now() });
      localStorage.setItem("nc_planner_library", JSON.stringify(lib.slice(0, 30)));
      toast.success("Saved to your library");
      logEvent("planner_page_uploaded");
      setPreview(null); setPageNo(""); setTitle(""); setNote(""); setTags("");
    } catch {
      toast.error("Storage full — try clearing your library");
    }
  };

  return (
    <div data-testid="upload-widget" className="rounded-2xl border-2 border-espresso bg-cream overflow-hidden">
      <div className="bg-champagne px-4 py-2.5 border-b-2 border-espresso flex items-center gap-2 font-mono text-xs">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-2 text-espresso">upload-page.exe</span>
      </div>
      <div className="p-6 space-y-3">
        <label className="block border-2 border-dashed border-espresso rounded-lg p-8 text-center cursor-pointer hover:bg-ivory">
          {preview ? (
            <img src={preview} alt="" className="max-h-40 mx-auto rounded" />
          ) : (
            <>
              <Upload size={24} className="mx-auto text-espresso mb-2" />
              <div className="text-espresso font-mono text-sm">Tap to choose an image</div>
              <div className="text-xs text-muted_brown font-mono mt-1">JPG / PNG · auto-compressed</div>
            </>
          )}
          <input type="file" accept="image/*" onChange={onFile} data-testid="upload-file" className="hidden" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <input value={pageNo} onChange={(e) => setPageNo(e.target.value)} placeholder="Page number" data-testid="upload-pageno" className="w-full bg-ivory border-2 border-espresso rounded-lg px-4 py-2.5 text-sm font-mono outline-none" />
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Weekly plan, study notes..." data-testid="upload-title" className="w-full bg-ivory border-2 border-espresso rounded-lg px-4 py-2.5 text-sm font-mono outline-none" />
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="What's this page about?" data-testid="upload-note" className="w-full bg-ivory border-2 border-espresso rounded-lg px-4 py-2.5 text-sm font-mono outline-none resize-none" />
        <input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="study, goal, exam" data-testid="upload-tags" className="w-full bg-ivory border-2 border-espresso rounded-lg px-4 py-2.5 text-sm font-mono outline-none" />
        <button onClick={save} data-testid="upload-save" className="bg-espresso text-cream font-mono text-xs uppercase tracking-wider px-5 py-2.5 rounded-lg hover:bg-espresso-dark transition-colors">
          Save to library
        </button>
      </div>
    </div>
  );
};

// ---------- QR widget ----------
const QRWidget = () => {
  const [value, setValue] = useState("");
  const [visibility, setVisibility] = useState("PRIVATE");
  const qrUrl = value
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(value)}&bgcolor=FDFBF7&color=3B2B20`
    : null;

  return (
    <div data-testid="qr-widget" className="rounded-2xl border-2 border-espresso bg-cream overflow-hidden">
      <div className="bg-champagne px-4 py-2.5 border-b-2 border-espresso flex items-center gap-2 font-mono text-xs">
        <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
        <span className="ml-2 text-espresso">create-qr.exe</span>
      </div>
      <div className="p-6 space-y-3">
        <input value={value} onChange={(e) => setValue(e.target.value)} placeholder="URL, text, or note to encode..." data-testid="qr-input" className="w-full bg-ivory border-2 border-espresso rounded-lg px-4 py-2.5 text-sm font-mono outline-none" />
        <div className="flex gap-2">
          {["PRIVATE", "SHAREABLE", "PASSWORD"].map((v) => (
            <button key={v} onClick={() => setVisibility(v)} data-testid={`qr-vis-${v}`} className={`flex-1 font-mono text-xs uppercase py-2.5 rounded-lg border-2 border-espresso ${visibility === v ? "bg-gold text-espresso" : "bg-cream text-espresso hover:bg-champagne"}`}>
              {v}
            </button>
          ))}
        </div>
        <div className="rounded-lg border-2 border-dashed border-espresso p-6 flex flex-col items-center justify-center bg-ivory min-h-[280px]">
          {qrUrl ? (
            <>
              <img src={qrUrl} alt="QR" className="w-56 h-56 rounded-lg" data-testid="qr-image" />
              <div className="text-xs font-mono text-muted_brown mt-3">{visibility.toLowerCase()} · scan to open</div>
            </>
          ) : (
            <>
              <QrCode size={40} className="text-muted_brown mb-3" />
              <div className="text-sm font-mono text-muted_brown">Your QR appears here.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------- MAIN PAGE ----------
export default function DigitalPlanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | locked | pending | approved | denied | not-logged-in
  const [reason, setReason] = useState("");

  useEffect(() => {
    (async () => {
      if (!user) { setStatus("not-logged-in"); return; }
      const email = (user.email || "").trim().toLowerCase();
      // Honor optimistic pending flag from a prior click so we don't flip back to locked on user refresh
      try {
        if (localStorage.getItem(`nc_dp_pending_${user.uid}`) === "1") {
          setStatus("pending");
        }
      } catch (_e) { void _e; }
      try {
        // 1) Check user doc
        const uref = doc(db, "users", user.uid);
        const usnap = await getDoc(uref);
        if (usnap.exists() && usnap.data()?.digitalAccess === "approved") {
          setStatus("approved"); setReason("granted");
          try { localStorage.removeItem(`nc_dp_pending_${user.uid}`); } catch (_e) { void _e; }
          return;
        }
        // 2) Check if user has any completed orders — auto-approve
        const ordersByUser = await getDocs(query(collection(db, "orders"), where("userId", "==", user.uid)));
        const orderSnaps = [ordersByUser];
        if (email) {
          orderSnaps.push(await getDocs(query(collection(db, "orders"), where("emailLower", "==", email))));
          orderSnaps.push(await getDocs(query(collection(db, "orders"), where("email", "==", email))));
          if (user.email !== email) {
            orderSnaps.push(await getDocs(query(collection(db, "orders"), where("email", "==", user.email))));
          }
        }
        const hasPlannerOrder = orderSnaps.some((snap) =>
          snap.docs.some((orderDoc) => {
            const order = orderDoc.data();
            if (order.status === "cancelled" || order.status === "refunded") return false;
            return (order.items || []).some((item) =>
              item.category === "planners" ||
              item.plannerType ||
              /planner|journal/i.test(item.name || "")
            );
          })
        );
        if (hasPlannerOrder) {
          await setDoc(uref, {
            digitalAccess: "approved",
            digitalApprovedAt: serverTimestamp(),
            digitalApprovedBy: "auto:purchase-email",
            digitalApprovedForEmail: email || user.email,
          }, { merge: true });
          setStatus("approved"); setReason("auto-approved: planner purchase found");
          try { localStorage.removeItem(`nc_dp_pending_${user.uid}`); } catch (_e) { void _e; }
          return;
        }
        if (usnap.exists() && usnap.data()?.digitalAccess === "denied") {
          setStatus("denied"); setReason("declined by admin");
          try { localStorage.removeItem(`nc_dp_pending_${user.uid}`); } catch (_e) { void _e; }
          return;
        }
        // 3) Check for existing request (only if we didn't already set pending optimistically)
        const requestSnaps = [await getDocs(query(collection(db, "digital_unlock_requests"), where("userId", "==", user.uid)))];
        if (email) requestSnaps.push(await getDocs(query(collection(db, "digital_unlock_requests"), where("email", "==", email))));
        const requests = requestSnaps
          .flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })))
          .filter((request, index, all) => all.findIndex((item) => item.id === request.id) === index)
          .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        if (requests.length > 0) {
          const anyApproved = requests.some((request) => request.status === "approved");
          if (anyApproved) {
            await setDoc(uref, { digitalAccess: "approved" }, { merge: true });
            setStatus("approved"); setReason("approved by admin");
            try { localStorage.removeItem(`nc_dp_pending_${user.uid}`); } catch (_e) { void _e; }
            return;
          }
          const hasPending = requests.some((request) => !request.status || request.status === "pending");
          if (hasPending) {
            setStatus("pending");
          } else {
            setStatus("denied"); setReason("declined by admin");
            try { localStorage.removeItem(`nc_dp_pending_${user.uid}`); } catch (_e) { void _e; }
          }
          return;
        }
        // Only downgrade to 'locked' if we don't already have an optimistic pending flag
        try {
          if (localStorage.getItem(`nc_dp_pending_${user.uid}`) !== "1") {
            setStatus("locked");
          }
        } catch {
          setStatus("locked");
        }
      } catch {
        try {
          if (localStorage.getItem(`nc_dp_pending_${user?.uid}`) !== "1") setStatus("locked");
        } catch {
          setStatus("locked");
        }
      }
    })();
  }, [user]);

  const requestUnlock = () => {
    if (!user) return navigate("/signup?redirect=/digital-planner");
    // Optimistically flip to pending — never block on Firestore
    setStatus("pending");
    try { localStorage.setItem(`nc_dp_pending_${user.uid}`, "1"); } catch (_e) { void _e; }
    toast.success("Request sent — admin will review within 24 hours.");
    logEvent("digital_unlock_requested");
    // Fire-and-forget the write; a hanging or denied write does not affect UX
    addDoc(collection(db, "digital_unlock_requests"), {
      userId: user.uid,
      email: (user.email || "").trim().toLowerCase(),
      name: user.displayName || "",
      status: "pending",
      createdAt: serverTimestamp(),
    }).catch(() => { /* fire-and-forget */ });
  };

  const FEATURES = useMemo(() => [
    { key: "diary", icon: Feather, t: "Write Diary", d: "Private daily entries with mood tagging. Search anything you've ever written.", node: <DiaryWidget /> },
    { key: "canvas", icon: Palette, t: "Mind Canvas", d: "Doodle your thoughts. Save your mind. Download as PNG anytime.", node: <CanvasWidget /> },
    { key: "upload", icon: Upload, t: "Upload Planner Page", d: "Snap a paper page — your planner lives on the cloud.", node: <UploadWidget /> },
    { key: "qr", icon: QrCode, t: "Memory QR", d: "Generate a shareable QR for any page, note, or memory.", node: <QRWidget /> },
  ], []);

  return (
    <div data-testid="digital-planner-page">
      {/* Hero */}
      <section className="bg-ivory border-b border-line">
        <div className="container-nc py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted_brown mb-6 uppercase tracking-[0.2em]">
              <Link to="/" className="hover:text-espresso">Home</Link>
              <ChevronRight size={12} />
              <span className="text-espresso">Digital Planner</span>
            </div>
            <p className="overline mb-5">The Note Console Companion</p>
            <h1 className="font-serif text-5xl md:text-6xl text-espresso leading-[1.05]">
              Your planner.
              <br /><span className="italic">On every device.</span>
            </h1>
            <p className="mt-6 text-muted_brown leading-relaxed max-w-md">
              Paper is where the day begins. But your goals shouldn&rsquo;t stay on your desk.
              Every Note Console planner ships with a free digital companion — a private diary,
              a doodle canvas, a page uploader, and a memory-QR generator.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {status === "approved" ? (
                <a href="#tools" className="btn-primary">Open the companion <ArrowRight size={16} /></a>
              ) : status === "not-logged-in" ? (
                <Link to="/login?redirect=/digital-planner" className="btn-primary" data-testid="hero-login-access">
                  <Lock size={14} /> Login to get access
                </Link>
              ) : (
                <button onClick={requestUnlock} className="btn-primary" data-testid="hero-unlock">
                  <Unlock size={14} /> Unlock complimentary
                </button>
              )}
              <Link to="/shop?cat=planners" className="btn-outline">Buy a planner</Link>
            </div>
            <div className="mt-8 flex items-center gap-5 text-xs text-muted_brown">
              <div className="flex items-center gap-2"><Check size={13} className="text-gold" /> Easy to access</div>
              <div className="flex items-center gap-2"><Check size={13} className="text-gold" /> Private by default</div>
              <div className="flex items-center gap-2"><Check size={13} className="text-gold" /> Yours Own Digital Space</div>
            </div>
          </div>

          {/* Mockup cluster */}
          <div className="relative aspect-square hidden lg:block">
            <img src={IMAGES.planner_open} alt="Planner" className="absolute w-3/4 aspect-[4/5] object-cover rounded-2xl shadow-medium -rotate-3 -translate-x-4" />
            <div className="absolute right-0 top-8 w-32 aspect-[9/19] rounded-[2rem] bg-cream border-4 border-espresso p-1.5 shadow-hover rotate-6 overflow-hidden">
              <div className="w-full h-full rounded-[1.5rem] bg-ivory relative">
                <div className="absolute top-3 left-3 right-3 text-[8px] font-mono uppercase text-muted_brown">// diary</div>
                <div className="absolute top-8 left-3 right-3 font-mono text-[10px] text-espresso leading-tight">write<br />today.</div>
                <div className="absolute bottom-4 left-3 right-3 space-y-1">
                  {["Calm", "Focused"].map((t, i) => <div key={i} className="text-[8px] font-mono text-espresso border border-espresso rounded px-1.5 py-0.5 inline-block mr-1">{t}</div>)}
                </div>
              </div>
            </div>
            <div className="absolute left-0 bottom-6 w-56 aspect-[16/11] rounded-xl bg-cream border-4 border-espresso p-2 shadow-hover -rotate-6">
              <div className="w-full h-full rounded-md bg-ivory relative overflow-hidden">
                <div className="absolute top-2 left-2 text-[8px] font-mono text-muted_brown">// canvas</div>
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 60">
                  <path d="M10,40 Q30,10 50,30 T90,20" fill="none" stroke="#D4AF37" strokeWidth="1.5" />
                  <path d="M15,50 Q40,45 65,55" fill="none" stroke="#3B2B20" strokeWidth="1" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Access banner */}
      {status !== "loading" && (
        <section className="border-b border-line" data-testid={`access-banner-${status}`}>
          <div className={`container-nc py-4 flex items-center gap-3 text-sm ${status === "approved" ? "text-espresso" : "text-muted_brown"}`}>
            {status === "approved" && (<><ShieldCheck size={16} className="text-gold" /> <span>Access unlocked{reason ? ` — ${reason}` : ""}.</span></>)}
            {status === "pending" && (<><Clock size={16} className="text-gold" /> <span>Request received. Admin will approve within 24 hours.</span></>)}
            {status === "locked" && (<><Lock size={16} className="text-muted_brown" /> <span>Digital planner is locked. Purchase any planner to unlock instantly.</span></>)}
            {status === "denied" && (<><Lock size={16} className="text-muted_brown" /> <span>Your unlock request was declined. Purchase a planner or contact support if this looks wrong.</span></>)}
            {status === "not-logged-in" && (<><Lock size={16} className="text-muted_brown" /> <span>Sign in to access your digital companion.</span> <Link to="/login?redirect=/digital-planner" className="ml-2 underline text-espresso">Log in</Link></>)}
          </div>
        </section>
      )}

      {/* Tools section — locked overlay if not approved */}
      <section id="tools" className="container-nc py-16 relative">
        <div className="max-w-2xl mb-12">
          <p className="overline mb-4">Premium preview</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
            Preview of our digital planner.
          </h2>
          <p className="text-muted_brown mt-4 leading-relaxed">
            A private, premium companion for your physical planner: diary, canvas, page uploads,
            QR memories, and your personal dashboard in one digital space.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-14">
          {DIGITAL_PREVIEWS.map((preview) => (
            <figure
              key={preview.title}
              className={`bg-ivory border border-line rounded-2xl overflow-hidden shadow-soft ${preview.className || ""}`}
            >
              <img
                src={preview.img}
                alt={`${preview.title} preview`}
                className="w-full max-h-[760px] object-contain object-top bg-cream"
                loading="lazy"
              />
              <figcaption className="px-5 py-4 font-serif text-xl text-espresso">
                {preview.title}
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="max-w-2xl mb-12">
          <p className="overline mb-4">One tool. One companion.</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
            Everything you plan on paper — <span className="italic">searchable.</span>
          </h2>
        </div>

        {status === "not-logged-in" ? (
          <div className="max-w-xl mx-auto text-center p-8 md:p-12 bg-cream border border-line rounded-2xl shadow-hover" data-testid="digital-login-lock-card">
            <div className="w-16 h-16 rounded-full gold-border flex items-center justify-center mx-auto mb-6">
              <Lock size={22} className="text-gold" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-3xl text-espresso">Companion locked</h3>
            <p className="text-muted_brown mt-4 leading-relaxed">
              Login to get access to your free digital companion.
            </p>
            <Link to="/login?redirect=/digital-planner" className="btn-primary mt-8" data-testid="lock-login">
              <Lock size={14} /> Login to get access
            </Link>
          </div>
        ) : (
          <div className={`relative grid grid-cols-1 md:grid-cols-2 gap-6 ${status !== "approved" ? "pointer-events-none select-none" : ""}`}>
          {status !== "approved" && (
            <div className="absolute inset-0 z-10 backdrop-blur-md bg-cream/30 rounded-2xl flex items-center justify-center" data-testid="digital-lock-overlay">
              <div className="text-center max-w-md p-8 bg-cream/95 border border-line rounded-2xl shadow-hover pointer-events-auto">
                <div className="w-14 h-14 rounded-full gold-border flex items-center justify-center mx-auto mb-4">
                  <Lock size={20} className="text-gold" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl text-espresso">Companion locked</h3>
                <p className="text-sm text-muted_brown mt-3 leading-relaxed">
                  {status === "locked" && "Purchase any Note Console planner and this unlocks instantly. Or request access and our admin will approve within 24 hours."}
                  {status === "pending" && "Your request is with our admin. You'll get access as soon as it's approved (within 24h)."}
                  {status === "denied" && "Your request was declined. If you already purchased a planner, sign in with the same email used at checkout or contact support."}
                </p>
                <div className="mt-6 flex flex-wrap gap-2 justify-center">
                  {status === "locked" && (
                    <>
                      <button onClick={requestUnlock} className="btn-primary text-xs py-2.5 px-5" data-testid="lock-request">
                        <Unlock size={13} /> Request access
                      </button>
                      <Link to="/shop?cat=planners" className="btn-outline text-xs py-2.5 px-5">Buy a planner</Link>
                    </>
                  )}
                  {status === "pending" && (
                    <div className="text-xs text-muted_brown flex items-center gap-2"><Clock size={13} className="text-gold" /> Waiting for approval</div>
                  )}
                  {status === "denied" && (
                    <Link to="/shop?cat=planners" className="btn-primary text-xs py-2.5 px-5">Buy a planner</Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {FEATURES.map((f) => (
            <div key={f.key} data-testid={`tool-card-${f.key}`} className="bg-ivory rounded-2xl p-6 border border-line">
              <div className="flex items-start gap-4 mb-5">
                <div className="w-11 h-11 rounded-full gold-border flex items-center justify-center shrink-0">
                  <f.icon size={16} strokeWidth={1.5} className="text-gold" />
                </div>
                <div>
                  <h3 className="font-serif text-2xl text-espresso">{f.t}</h3>
                  <p className="text-sm text-muted_brown mt-1">{f.d}</p>
                </div>
              </div>
              {f.node}
            </div>
          ))}
          </div>
        )}
      </section>

      {/* Cross-device */}
      <section className="bg-espresso text-cream">
        <div className="container-nc py-20 grid grid-cols-1 md:grid-cols-4 gap-8">
          {[
            { i: Smartphone, t: "iOS & Android" },
            { i: Cloud, t: "Cloud-backed" },
            { i: Sparkles, t: "Offline-first" },
            { i: ShieldCheck, t: "Private by design" },
          ].map(({ i: Icon, t }) => (
            <div key={t} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full gold-border flex items-center justify-center">
                <Icon size={14} strokeWidth={1.5} className="text-gold" />
              </div>
              <div className="text-sm">{t}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Complimentary CTA */}
      <section className="bg-champagne/50">
        <div className="container-nc py-20 text-center max-w-2xl mx-auto">
          <p className="overline mb-4">Complimentary with any planner</p>
          <h2 className="font-serif text-4xl md:text-5xl text-espresso leading-tight">
            Buy a planner. <span className="italic">Get the companion free.</span>
          </h2>
          <p className="text-muted_brown mt-4">
            The digital companion is included with every Note Console planner — no subscription,
            no upsell, no lock-in. Yours forever, on every device you own.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/shop?cat=planners" className="btn-primary">Shop planners <ArrowRight size={16} /></Link>
            {status === "not-logged-in" ? (
              <Link to="/login?redirect=/digital-planner" className="btn-outline" data-testid="cta-login-access">
                <Lock size={14} /> Login to get access
              </Link>
            ) : status !== "approved" && (
              <button onClick={requestUnlock} className="btn-outline" data-testid="cta-unlock">
                <Unlock size={14} /> Or request access
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
